import { ChatSystemProvider } from "@innerhour/chat-system";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { NumberParam, useQueryParam } from "use-query-params";

import axiosInstance from "../../axiosInstance";
import { openLink, trackEvent } from "../../helperFunctions";
import useMobileView from "../../hooks/useMobileView";
import { apiURL, centrifugoUrl } from "../../variable";
import ChatNew from "../Chat/ChatNew";
import StatusModal from "../Chat/StatusModal";
import FlexBox from "../common/FlexBox";
import Loader from "../common/Loader";

const Chat = lazy(() => import("../Chat"));
const ChatList = lazy(() => import("./ChatList"));

const Wrapper = styled(FlexBox)`
  flex: 1;
  max-width: 100%;
  height: 100%;
`;

const DashboardChatHandler = ({
  selectedProfileClient = null,
  isChatOpen,
  closeChat,
  fromProfile = false,
  cannedMessage,
  chatClientSelected = () => {},
}) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedChatClient, setSelectedChatClient] = useState(null);
  const [pendingCannedMessage, setPendingCannedMessage] = useState(
    cannedMessage || null
  );

  const hasMountedRef = useRef(false);
  const navigate = useNavigate();
  const authUser = useSelector(state => state.auth?.user);
  const providerProfile = useSelector(state => state.provider?.providerProfile);
  const [selectedRoomId, setSelectedRoomId] = useQueryParam(
    "room_id",
    NumberParam
  );
  const isMobile = useMobileView();
  const newChatEnabled = useSelector(
    state => !!state.provider?.providerProfile?.new_chat_enabled
  );

  const providerId = authUser?.id || "";
  const providerUuid = authUser?.uuid || "";
  const providerName = authUser?.name || "";
  const providerImage = providerProfile?.image || "";

  const exitChat = useCallback(() => {
    setSelectedChatClient(null);
    setSelectedRoomId(null, "replaceIn");
  }, [setSelectedRoomId]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    exitChat();
  }, [cannedMessage, exitChat]);

  useEffect(() => {
    setPendingCannedMessage(cannedMessage || null);
  }, [cannedMessage]);

  useEffect(() => {
    const isClientSelected = newChatEnabled
      ? !!selectedRoomId
      : !!selectedChatClient;
    chatClientSelected(isClientSelected);
  }, [chatClientSelected, selectedChatClient, selectedRoomId, newChatEnabled]);

  const selectedChat = selectedProfileClient || selectedChatClient;

  const toggleStatusModal = () => setShowStatusModal(!showStatusModal);

  return (
    <Wrapper>
      {showStatusModal && <StatusModal toggleModal={toggleStatusModal} />}

      {authUser && newChatEnabled ? (
        <ChatSystemProvider
          config={{
            axiosInstance,
            apiUrl: apiURL,
            centrifugoUrl: centrifugoUrl,
            navigate: (url, method = "push") =>
              navigate(url, { replace: method === "replace" }),
            trackEvent,
            openLink,
            currentUser: {
              id: authUser.id,
              name: authUser.name,
              role: "provider",
            },
            isMobile,
          }}
        >
          <ChatNew
            selectedRoomId={selectedRoomId}
            setSelectedRoomId={roomId => setSelectedRoomId(roomId, "replaceIn")}
            fromProfile={fromProfile}
            closeChat={closeChat}
            cannedMessage={pendingCannedMessage}
            onCannedMessageConsumed={() => setPendingCannedMessage(null)}
            providerId={providerId}
            providerUuid={providerUuid}
            providerName={providerName}
            providerImage={providerImage}
            toggleStatusModal={toggleStatusModal}
          />
        </ChatSystemProvider>
      ) : (
        <Suspense fallback={<Loader />}>
          {!!selectedChat && (
            <Chat
              handleBack={exitChat}
              isChatOpen={isChatOpen}
              closeChat={closeChat}
              exitChat={exitChat}
              client={selectedChat}
              cannedMessage={cannedMessage}
            />
          )}

          {!fromProfile && (
            <ChatList
              exitChat={exitChat}
              selectedChat={selectedChat}
              selectChat={setSelectedChatClient}
              toggleStatusModal={toggleStatusModal}
            />
          )}
        </Suspense>
      )}
    </Wrapper>
  );
};

export default DashboardChatHandler;
